import React, { useRef, useState } from 'react';
import { resolveMediaUrl } from '../../utils/mediaUrl';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';

const EventImageUpload = ({
  inputId = 'event-image-upload',
  imagePreview,
  onFileSelect,
  onRemove,
  label = 'Event poster'
}) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const pickFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    onFileSelect(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  const openPicker = () => inputRef.current?.click();
  const displaySrc = resolveMediaUrl(imagePreview);

  if (imagePreview) {
    return (
      <div className="space-y-2">
        <p className="text-xs font-semibold text-gray-600">{label}</p>
        <div className="relative inline-block rounded-lg overflow-hidden border border-gray-200 group max-w-xs w-full">
          <img
            src={displaySrc}
            alt="Event poster preview"
            className="w-full h-36 object-cover bg-gray-100"
            onError={(e) => {
              if (displaySrc !== imagePreview && imagePreview.startsWith('data:')) {
                e.currentTarget.src = imagePreview;
              }
            }}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
            <button
              type="button"
              onClick={openPicker}
              className="px-3 py-1.5 text-xs font-medium bg-white text-gray-800 rounded-md shadow"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-md shadow"
            >
              Remove
            </button>
          </div>
        </div>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          onChange={(e) => pickFile(e.target.files?.[0])}
        />
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold text-gray-600">{label}</p>
      <div
        role="button"
        tabIndex={0}
        onClick={openPicker}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openPicker(); }}
        onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
        onDrop={handleDrop}
        className={`flex flex-col items-center justify-center w-full h-28 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
          isDragging
            ? 'border-orange-500 bg-orange-50'
            : 'border-gray-300 bg-gray-50/80 hover:border-orange-400 hover:bg-orange-50/50'
        }`}
      >
        <i className={`fas fa-cloud-upload-alt text-2xl mb-1.5 ${isDragging ? 'text-orange-500' : 'text-gray-400'}`} />
        <span className={`text-sm font-medium ${isDragging ? 'text-orange-600' : 'text-orange-600'}`}>
          {isDragging ? 'Drop image here' : 'Click or drag & drop to upload'}
        </span>
        <span className="text-[11px] text-gray-400 mt-0.5">PNG, JPG, WEBP — auto-converted to crisp WebP (max 12MB)</span>
      </div>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        onChange={(e) => pickFile(e.target.files?.[0])}
      />
    </div>
  );
};

export default EventImageUpload;
